import clsx from "clsx";
import React from "react";
import { Box, Grid2Props, Theme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import ToggleIconButtonGroup from "../button/ToggleIconButtonGroup";
import { LibraryHeaderFilters } from "../header-filters/HeaderFilters";
import ResetFiltersButton from "../exercise-library/filters/ResetFiltersButton";

type StyleProps = {
  inlineActions: boolean;
};

const height = 40;

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  sort: {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 500,
    flexGrow: 1,
    width: ({ inlineActions }) => (!inlineActions ? "100%" : undefined),
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "space-between",
    },
  },

  rowInline: {
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
      flexDirection: "row",
    },
  },

  toggle: {
    color: theme.palette.text.secondary,
    minWidth: "auto",

    fill: theme.palette.text.secondary,
    "&.active": {
      fill: theme.palette.primary.main,
    },

    "& svg": {
      width: 20,
      height: 20,
    },
  },

  resetFilters: {
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    height: height,
  },
}));

export interface ToggleOption<T> {
  value: T;
  tooltip?: string;
  label?: string;
  icon?: React.ReactNode;
}

export interface CoachLibraryHeaderProps<T> extends Grid2Props {
  query: string;
  defaultQuery?: string;
  searchPlaceholder: string;
  onChangeQuery?: (query: string) => void;
  showActions?: boolean;
  inlineActions?: boolean;
  toggleOptions?: ToggleOption<T>[];
  toggleValue?: T;
  onChangeToggle?: (value: T) => void;
  toggleClassName?: string;
  showResetButton?: boolean;
  disabledResetButton?: boolean;
  onResetFilters?: () => void;
  resetButtonClassName?: string;
  actionsContainerClassName?: string;
}

export function CoachLibraryHeader<T>(props: CoachLibraryHeaderProps<T>) {
  const {
    className,
    query,
    defaultQuery = "",
    searchPlaceholder,
    onChangeQuery,
    showActions = true,
    inlineActions = false,
    toggleOptions,
    toggleValue,
    onChangeToggle,
    toggleClassName,
    showResetButton,
    disabledResetButton,
    onResetFilters,
    resetButtonClassName,
    actionsContainerClassName,
    ...other
  } = props;

  const s = useStyles({ inlineActions });

  const shouldRenderToggle =
    !!toggleOptions &&
    onChangeToggle !== undefined &&
    toggleValue !== undefined;

  const shouldRenderActions =
    showActions && (shouldRenderToggle || showResetButton);

  return (
    <LibraryHeaderFilters
      query={query}
      onChangeQuery={onChangeQuery}
      className={clsx(className, inlineActions && s.rowInline)}
      searchPlaceholder={searchPlaceholder}
      defaultValues={{
        query: defaultQuery,
      }}
      selectItem={
        shouldRenderActions ? (
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            className={clsx(s.sort, actionsContainerClassName)}
          >
            {shouldRenderToggle && (
              <ToggleIconButtonGroup
                className={clsx(s.toggle, toggleClassName)}
                options={toggleOptions}
                value={(toggleValue as T) || null}
                onChange={onChangeToggle as (value: T | null) => void}
                displayLabel="always"
              />
            )}

            {showResetButton && (
              <ResetFiltersButton
                disabled={disabledResetButton}
                onClick={onResetFilters}
                className={clsx(s.resetFilters, resetButtonClassName)}
              />
            )}
          </Box>
        ) : undefined
      }
      {...other}
    />
  );
}
